/**
 * Extracts user IDs from text using the cfto_user_id pattern
 * @param {string} text - The text to extract user IDs from
 * @returns {Object} Object with user IDs as keys and true as values (maintains original behavior)
 */
const extractUserIdsFromText = (text) => {
  if (!text) return {};

  const regex = /cfto_user_id="\s*(.*?)\s*"/g;
  const usersObject = {};
  let match;

  do {
    match = regex.exec(text);
    if (match) {
      usersObject[match[1]] = true;
    }
  } while (match);

  return usersObject;
};

/**
 * Converts user IDs object to emails array using the project users list
 * @param {Object} usersObject - Object with user IDs as keys
 * @param {Array} projectUsersList - Array of user objects with id and email properties
 * @returns {Array<string>} Array of unique email addresses
 */
const convertUserIdsToEmails = (usersObject, projectUsersList) => {
  if (
    !usersObject ||
    Object.keys(usersObject).length === 0 ||
    !projectUsersList?.length
  ) {
    return [];
  }

  const emailsObject = {};

  // Use for loop to maintain exact original behavior
  for (let i = 0; i < projectUsersList.length; i++) {
    const user = projectUsersList[i];
    if (user.id in usersObject) {
      emailsObject[user.email] = true;
    }
  }

  return Object.keys(emailsObject);
};

/**
 * Unified function to get emails for tagged users from notes and replies
 * @param {Object} options - Configuration object
 * @param {string} [options.noteBody] - The text of the note body
 * @param {Object} [options.replies] - The object that contains all the replies for the note
 * @param {Array} options.projectUsersList - The array of all user details
 * @param {string} [options.previousNoteBody] - The text of the previous note body (used for note editing)
 * @param {number} [options.replyIndex] - The index of a specific reply to check (if provided, only that reply is checked)
 * @param {boolean} [options.includeLastReply=true] - Whether to include the last reply when processing note body
 * @returns {Array<string>} Array of email addresses for tagged users
 */
export const getEmailsForTaggedUsers = ({
  noteBody = '',
  replies = {},
  projectUsersList = [],
  previousNoteBody = '',
  replyIndex = null,
  includeLastReply = true,
}) => {
  let usersObject = {};
  let usersObjectInPreviousNote = {};

  // If any replyIndex is specified, only process that specific reply
  if (replyIndex !== null) {
    if (!replies) {
      return [];
    }
    const repliesKeys = Object.keys(replies);
    if (
      repliesKeys?.length > 0 &&
      replyIndex >= 0 &&
      replyIndex < repliesKeys?.length
    ) {
      const replyKey = repliesKeys[replyIndex];
      const reply = replies[replyKey];

      if (reply?.reply) {
        usersObject = extractUserIdsFromText(reply.reply);
      }

      if (reply?.previousReply !== '') {
        usersObjectInPreviousNote = extractUserIdsFromText(reply.previousReply);
      }

      // Remove users that were already tagged in existing note
      if (Object.keys(usersObjectInPreviousNote).length !== 0) {
        const currentUsersInNoteBody = Object.keys(usersObject);
        for (let i = 0; i < currentUsersInNoteBody.length; i++) {
          const userId = currentUsersInNoteBody[i];
          if (userId in usersObjectInPreviousNote) {
            delete usersObject[userId];
          }
        }
      }
    }
    return convertUserIdsToEmails(usersObject, projectUsersList);
  }

  // Process new/existing note
  if (noteBody !== '') {
    usersObject = extractUserIdsFromText(noteBody);
  }

  // Processing previous note (for excluding already tagged users within the note body)
  if (previousNoteBody !== '') {
    usersObjectInPreviousNote = extractUserIdsFromText(previousNoteBody);
  }

  // Processing last reply if includeLastReply is true and replies exist
  if (includeLastReply) {
    const repliesKeys = Object.keys(replies);
    if (repliesKeys.length > 0) {
      const lastReplyKey = repliesKeys[repliesKeys.length - 1];
      const reply = replies[lastReplyKey];
      if (reply?.reply) {
        const replyUsersObject = extractUserIdsFromText(reply.reply);
        // Merge reply users with note body users
        Object.assign(usersObject, replyUsersObject);
      }
    }
  }

  // Remove users that were already tagged in previous note body to avoid duplicate emails
  if (Object.keys(usersObjectInPreviousNote).length !== 0) {
    const currentUsersInNoteBody = Object.keys(usersObject);
    for (let i = 0; i < currentUsersInNoteBody.length; i++) {
      const userId = currentUsersInNoteBody[i];
      if (userId in usersObjectInPreviousNote) {
        delete usersObject[userId];
      }
    }
  }

  return convertUserIdsToEmails(usersObject, projectUsersList);
};

// this function is specifically used for note replies and getting the emails of tagged users in that particular reply
export const getEmailsForTaggedUsersFromReply = ({
  replies,
  projectUsersList,
  index,
}) => {
  return getEmailsForTaggedUsers({
    replies,
    projectUsersList,
    replyIndex: index,
    includeLastReply: false,
  });
};
